/** Échéance par défaut, type de paiement, validité devis. */
import { Field } from "../../components/Field";
import { PAYMENT_KINDS, type CompanyPayments, type PaymentKind, type PaymentTerm } from "../../payments/types";

type PaymentsPolicyProps = {
  draft: CompanyPayments;
  setDraft: (next: CompanyPayments) => void;
  terms: PaymentTerm[];
};

const CLASSIC_TERM_DAYS = new Set([0, 7, 10, 14, 15, 30, 45, 60, 90]);

export function PaymentsPolicy({ draft, setDraft, terms }: PaymentsPolicyProps) {
  const shown = terms.filter((t) => CLASSIC_TERM_DAYS.has(Number(t.days)));
  const termId = draft.default_payment_term_id ?? shown.find((t) => t.is_default)?.id ?? shown[0]?.id ?? 0;

  return (
    <>
      <div className="fx-fmt-row">
        <span>Conditions de paiement</span>
        <label className="fx-field flex flex-col gap-1 text-xs">
          Échéance par défaut
          <select
            className="rounded-md border px-2.5 py-2 text-sm"
            value={termId || ""}
            onChange={(e) => setDraft({ ...draft, default_payment_term_id: Number(e.target.value) || null })}
          >
            {shown.length === 0 ? <option value="">Aucune échéance</option> : null}
            {shown.map((t) => (
              <option key={t.id} value={t.id}>
                {t.label}
                {Number(t.days) ? ` · ${t.days} j` : ""}
                {t.end_of_month ? " · fin de mois" : ""}
              </option>
            ))}
          </select>
          <span className="text-slate-500">
            Date de paiement attendue (virement, GoCardless, PayPal, Stripe). Ce n’est pas le jour de création ni d’envoi.
          </span>
        </label>
      </div>
      <div className="fx-fmt-row">
        <span>Type de paiement</span>
        <label className="fx-field flex flex-col gap-1 text-xs">
          Par défaut
          <select
            className="rounded-md border px-2.5 py-2 text-sm"
            value={draft.default_payment_kind}
            onChange={(e) => setDraft({ ...draft, default_payment_kind: e.target.value as PaymentKind })}
          >
            {PAYMENT_KINDS.map((k) => (
              <option key={k.id} value={k.id}>
                {k.label}
              </option>
            ))}
          </select>
          <span className="text-slate-500">Type proposé à l’enregistrement d’un paiement.</span>
        </label>
      </div>
      <div className="fx-fmt-row fx-fmt-row--top">
        <span>Mentions de paiement</span>
        <div className="flex w-full flex-col gap-3">
          <Field
            label="Pénalités de retard"
            value={draft.late_penalty_label}
            onChange={(e) => setDraft({ ...draft, late_penalty_label: e.target.value.slice(0, 128) })}
            hint="Écrit automatiquement sur facture et avoir, avec l’indemnité 40 €."
          />
          <Field
            label="Escompte pour paiement anticipé"
            type="number"
            min={0}
            max={10000}
            step={100}
            value={String(draft.early_discount_bp)}
            onChange={(e) => setDraft({ ...draft, early_discount_bp: Math.min(10000, Math.max(0, Number(e.target.value) || 0)) })}
            hint={
              draft.early_discount_bp > 0
                ? `${(draft.early_discount_bp / 100).toLocaleString("fr-FR")} %. Écrit automatiquement. 0 = aucun escompte.`
                : "0 = aucun escompte (mention automatique). 100 = 1 %."
            }
          />
        </div>
      </div>
      <div className="fx-fmt-row">
        <span>Offre valable jusqu’au</span>
        <Field
          label="Jours de validité du devis"
          type="number"
          min={1}
          max={365}
          value={String(draft.quote_valid_days)}
          onChange={(e) => setDraft({ ...draft, quote_valid_days: Math.min(365, Math.max(1, Number(e.target.value) || 30)) })}
        />
      </div>
    </>
  );
}
